import React, { useRef, useEffect, useState, FC } from 'react';
import { useStore } from 'zustand';
import { useMapStore } from 'stores/map/mapStore';
import { GeoLocation, SelectedPlace } from '../type';
import { GOOGLE_MAPS_API } from '@/utils/constants';

interface Props {
  className?: string;
  onPlaceSelected?: (selected: SelectedPlace) => void;
}

interface AutocompletePrediction {
  description: string | undefined;
  place_id: string | undefined;
  mainText: string | undefined;
  secondaryText: string | undefined;
}

export const PlaceAutocompleteCustom: FC<Props> = ({
  className,
  onPlaceSelected,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const { setSelected } = useStore(useMapStore, (state) => ({
    setSelected: state.setSelected,
  }));
  const [predictions, setPredictions] = useState<AutocompletePrediction[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showDropdown, setShowDropdown] = useState(false);
  const twClass =
    className ||
    'inline-block h-full w-full border border-x-0 border-gray-200 px-4 py-1 text-sm focus-visible:outline-none focus-visible:ring-0 disabled:pointer-events-none';

  useEffect(() => {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API}&loading=async&libraries=places-new`;
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  const handleInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const request = {
      input: event.target.value,
      includedRegionCodes: ['us', 'ca'],
    };

    const { suggestions } =
      await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
        request
      );

    const predictions: AutocompletePrediction[] = suggestions.map(
      (suggestion) => {
        const place = suggestion.placePrediction;

        return {
          description: place?.text.text,
          mainText: place?.mainText?.text,
          secondaryText: place?.secondaryText?.text,
          place_id: place?.placeId,
        };
      }
    );

    predictions && setPredictions(predictions);
  };

  const handleSelect = (prediction: any) => {
    inputRef.current!.value = prediction.description;
    setShowDropdown(false);
    setSelectedIndex(-1);
    prediction.place_id && handlePlaceSelect(prediction.place_id);
  };

  const handlePlaceSelect = async (placeId: string) => {
    const place = new google.maps.places.Place({
      id: placeId,
      requestedLanguage: 'en',
    });

    await place.fetchFields({
      fields: ['formattedAddress', 'location'],
    });

    const selected: SelectedPlace = {
      address: place.formattedAddress || '',
      geometry: place.location?.toJSON() as GeoLocation,
      manualEntry: true,
    };

    onPlaceSelected?.(selected);

    selected.geometry.lat && selected.geometry.lng && setSelected(selected);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!predictions.length) return;

    if (e.key) {
      setShowDropdown(true);
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev < predictions.length - 1 ? prev + 1 : prev
      );
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
    }

    if (e.key === 'Enter' && selectedIndex !== -1) {
      e.preventDefault();
      const prediction = predictions[selectedIndex];
      prediction.place_id && handlePlaceSelect(prediction.place_id);
      inputRef.current!.value = prediction?.description || '';
      setSelectedIndex(-1);
      setShowDropdown(false);
    }
  };

  return (
    <div className='relative inline-block h-full w-full'>
      <input
        ref={inputRef}
        placeholder='Enter a zip code, city, or address to search'
        type='text'
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setShowDropdown(true)}
        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
        className={twClass}
      />
      {showDropdown && predictions.length > 0 && (
        <ul
          ref={listRef}
          className={`absolute top-full z-50 w-full border border-gray-200 bg-white shadow-lg`}
        >
          {predictions.map((prediction, idx) => (
            <li
              key={prediction.place_id}
              onClick={() => handleSelect(prediction)}
              onMouseDownCapture={() => setSelectedIndex(idx)}
              className={`cursor-pointer p-2 ${
                selectedIndex === idx ? 'bg-gray-200' : 'hover:bg-gray-100'
              }`}
            >
              {prediction.description}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
